import type { StatusTransition } from '@atlassian/jira-issue-field-status/src/common/types.tsx';

const shouldReplace = (existing: StatusTransition, replacement: StatusTransition): boolean =>
	(!existing.isInitial && replacement.isInitial) ||
	(existing.isDisabled === true && !replacement.isDisabled);

type ValidationEntry = {
	unspecified: boolean;
	fieldValidations: Array<{
		fields: string[];
		type: string;
		errorMessage: string;
	}>;
};

type TransitionWithValidations = StatusTransition & {
	validations?: ValidationEntry[];
};

type ApiResponse = TransitionWithValidations[];

export type TransitionResponse = {
	transitions: StatusTransition[];
	fieldRequiredValidators?: string[];
};

export const transformDataWithValidations = (
	data: ApiResponse,
): StatusTransition[] | TransitionResponse => {
	if (!Array.isArray(data)) {
		return [];
	}

	const transitionMap = new Map<string, StatusTransition>();
	const requiredFields = new Set<string>();

	for (const val of data) {
		// Extract validations if needed (during the same iteration)
		if (requiredFields && val.validations?.length) {
			for (const validation of val.validations) {
				for (const fieldValidation of validation.fieldValidations || []) {
					if (fieldValidation.type === 'FIELDREQUIREDVALIDATOR') {
						fieldValidation.fields.forEach((field) => requiredFields.add(field));
					}
				}
			}
		}

		// Process transition (clean object without validations)
		const cleanTransition: StatusTransition = {
			id: val.id,
			isConditional: val.isConditional,
			isGlobal: val.isGlobal,
			isInitial: val.isInitial,
			hasScreen: val.hasScreen,
			isLooped: val.isLooped,
			name: val.name,
			to: val.to,
			isDisabled: val.isDisabled,
			unavailableReasonText: val.unavailableReasonText,
			statusCategory: val.statusCategory,
		};

		const existingTransition = transitionMap.get(cleanTransition.to.id);
		if (!existingTransition || shouldReplace(existingTransition, cleanTransition)) {
			transitionMap.set(cleanTransition.to.id, cleanTransition);
		}
	}

	const transitions = Array.from(transitionMap.values());

	// Return enhanced response if field validators requested and found
	return { transitions, fieldRequiredValidators: Array.from(requiredFields) };
};

export const transformData = (data: StatusTransition[]): StatusTransition[] => {
	const transitionMap = data.reduce((acc, val) => {
		if (acc.has(val.to.id) && !shouldReplace(acc.get(val.to.id), val)) {
			return acc;
		}

		return acc.set(val.to.id, val);
	}, new Map());

	return Array.from(transitionMap.values());
};
